import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'

/**
 * Formats a date.  ex. Jan 1, 2024
 * @param d
 * @returns
 */
export function formatDate(d: Date): string {
  try {
    return format(d, 'MMM d, yyyy')
  } catch (e) {
    console.warn('Failed to format date', d)
    return ''
  }
}

/**
 * Formats a date string.  ex. Jan 1, 2024
 * @param s
 * @returns
 */
export function formatDateString(s: string): string {
  try {
    return formatDate(parseISO(s))
  } catch (e) {
    console.warn('Failed to format date string', s)
    return ''
  }
}

/**
 * Formats a date range.  ex. Dec 1 - Dec 5, 2024
 * @param startDateString
 * @param endDateString
 * @param separator
 * @returns
 */
export function formatDateRange(
  startDateString: string,
  endDateString: string,
  separator: string = '-'
): string {
  try {
    const startDate = parseISO(startDateString)
    const formattedStartDate = format(startDate, 'MMM d')
    const endDate = parseISO(endDateString)
    const formattedEndDate = format(endDate, 'MMM d, yyyy')
    return `${formattedStartDate} ${separator} ${formattedEndDate}`
  } catch (e) {
    console.warn('Failed to parse and format date range', {
      startDateString,
      endDateString
    })
    return ''
  }
}

/**
 * Formats a stay date and the property's standard check-in/check-out time.
 * ex. Wed, Dec 1 2024, 3:00 PM
 *
 * The two parts are joined deliberately without a UTC offset. A zone-naive
 * datetime carries no instant for the browser to convert, so 15:00:00 renders
 * as 3:00 PM for every guest regardless of where they are browsing from — which
 * is not true of the offset-bearing datetimes Apaleo returns.
 *
 * @param dateString date-only string, ex. 2026-08-03
 * @param time ISO 8601 local time, ex. 15:00:00
 */
export function formatDayAndTime(dateString: string, time: string): string {
  // Both parts are required. parseISO reads a trailing 'T' as midnight rather
  // than rejecting it, so a missing time would silently render as 12:00 AM.
  if (!dateString || !time) {
    return ''
  }

  try {
    return format(parseISO(`${dateString}T${time}`), 'EEE, MMM d yyyy, h:mm a')
  } catch (e) {
    console.warn('Failed to format stay date', {dateString, time})
    return ''
  }
}
